import { useCallback, useEffect, useState } from "react"
import type { ReactNode } from "react"
import FullscreenIcon from "@mui/icons-material/Fullscreen"
import MetadataAwareTokenView from "components/MetadataAwareTokenView"
import ArtworkFullscreen from "components/ArtworkFullscreen"
import useWindowSize from "hooks/useWindowSize"
import { getArtStageSize } from "utils/galleryLayout"
import type { NftTokenMetadata } from "utils/tokenMetadata"

interface Props {
  contractAddress: string
  tokenId: string
  chainId?: number
  aspectRatio: number
  title: string
  metadata?: NftTokenMetadata | null
  metadataLoading?: boolean
  hasLiveView: boolean
  generatorHref?: string
  imageHref: string
  /** Small line shown under the art (e.g. "Owned by …"). */
  caption?: ReactNode
  onPrev?: () => void
  onNext?: () => void
}

const ArtHeroStage = ({
  contractAddress,
  tokenId,
  chainId,
  aspectRatio,
  title,
  metadata,
  metadataLoading,
  hasLiveView,
  generatorHref,
  imageHref,
  caption,
  onPrev,
  onNext,
}: Props) => {
  const windowSize = useWindowSize()
  const [renderMode, setRenderMode] = useState<"image" | "live">("image")
  const [fullscreen, setFullscreen] = useState(false)
  const [hintLabel, setHintLabel] = useState<string | null>(null)
  const [hintVisible, setHintVisible] = useState(false)

  useEffect(() => {
    if (metadataLoading) return
    setRenderMode(hasLiveView ? "live" : "image")
  }, [hasLiveView, metadataLoading])

  useEffect(() => {
    setFullscreen(false)
  }, [tokenId])

  const dismissHint = useCallback(() => {
    setHintVisible(false)
    try {
      window.localStorage.setItem("r4v3n-loupe-hint", "1")
    } catch {
      /* ignore */
    }
    setTimeout(() => setHintLabel(null), 420)
  }, [])

  // One-time discoverability nudge for the ambient loupe.
  useEffect(() => {
    if (metadataLoading) return
    try {
      if (window.localStorage.getItem("r4v3n-loupe-hint")) return
    } catch {
      /* ignore */
    }
    const isTouch = window.matchMedia?.("(hover: none)").matches
    setHintLabel(isTouch ? "Touch to zoom" : "Hover to zoom")
    const showTimer = setTimeout(() => setHintVisible(true), 650)
    const hideTimer = setTimeout(() => dismissHint(), 4400)
    return () => {
      clearTimeout(showTimer)
      clearTimeout(hideTimer)
    }
  }, [metadataLoading, dismissHint])

  const { width, height } = getArtStageSize(windowSize.width, windowSize.height, aspectRatio)
  const liveDisabled = !hasLiveView

  const renderArt = (artWidth: number, artHeight: number, withLoupe: boolean) => (
    <MetadataAwareTokenView
      contractAddress={contractAddress}
      tokenId={tokenId}
      chainId={chainId}
      width={artWidth}
      maxHeight={artHeight}
      fallbackAspectRatio={aspectRatio}
      live={renderMode === "live"}
      metadata={metadata}
      metadataLoading={metadataLoading}
      showInvocationLink={false}
      loupe={withLoupe}
      loupeZoom={3}
    />
  )

  return (
    <section className="gallery-art-stage" aria-label="Artwork">
      <div className="gallery-art-stage-art">
        <div className="gallery-art-shell relative" onPointerDown={hintLabel ? dismissHint : undefined}>
          <div className="gallery-art-frame">{renderArt(width, height, true)}</div>
          {hintLabel && (
            <div className="gallery-loupe-hint" data-visible={hintVisible ? "true" : "false"}>
              {hintLabel}
            </div>
          )}
        </div>
      </div>

      <div className="gallery-art-stage-controls">
        {caption ? <div className="text-sm text-gallery-muted">{caption}</div> : <span />}
        <div className="flex flex-wrap items-center gap-3">
          <div className="gallery-segment-group" aria-label="Render mode">
            <button
              type="button"
              aria-pressed={renderMode === "image"}
              className="gallery-segment-button"
              onClick={() => setRenderMode("image")}
            >
              Image
            </button>
            <button
              type="button"
              aria-pressed={renderMode === "live"}
              disabled={liveDisabled}
              className="gallery-segment-button"
              onClick={() => setRenderMode("live")}
            >
              Live
            </button>
          </div>
          <button
            type="button"
            aria-label="View fullscreen"
            title="Fullscreen"
            className="gallery-inspect-button"
            onClick={() => setFullscreen(true)}
          >
            <FullscreenIcon sx={{ fontSize: 14 }} />
            Fullscreen
          </button>
          {generatorHref && (
            <a
              href={generatorHref}
              target="_blank"
              rel="noopener noreferrer"
              className="gallery-label text-gallery-muted hover:text-gallery-text transition-colors"
            >
              Live view →
            </a>
          )}
          <a
            href={imageHref}
            target="_blank"
            rel="noopener noreferrer"
            className="gallery-label text-gallery-muted hover:text-gallery-text transition-colors"
          >
            Image →
          </a>
        </div>
      </div>

      {fullscreen && (
        <ArtworkFullscreen
          title={title}
          caption={caption}
          aspectRatio={aspectRatio}
          viewportWidth={windowSize.width}
          viewportHeight={windowSize.height}
          renderArtwork={(w, h) => renderArt(w, h, false)}
          onClose={() => setFullscreen(false)}
          onPrev={onPrev}
          onNext={onNext}
        />
      )}
    </section>
  )
}

export default ArtHeroStage
